// src/services/s3CredentialsManager.service.ts - Centralized S3 session management
import { UPLOAD_CONFIG } from '../constants/upload.constants';

export interface S3Credentials {
  AccessKeyId: string;
  SecretAccessKey: string;
  SessionToken: string;
  Expiration: string;
}

export interface UploadCredentialsResponse {
  success: boolean;
  data: {
    credentials: S3Credentials;
    uploadId: string;
    bucket: string;
    region: string;
    prefix: string;
  };
}

export interface GalleryCredentialsResponse {
  success: boolean;
  data: {
    credentials: S3Credentials;
    bucket: string;
    region: string;
    sessionType: string;
    pooled: boolean;
  };
}

export interface ThumbnailCredentialsResponse {
  success: boolean;
  data: {
    credentials: S3Credentials;
    bucket: string;
    region: string;
    folderPath: string;
    sessionType: string;
  };
}

export interface SessionStatsResponse {
  success: boolean;
  data: {
    upload: { total: number; active: number; completed: number; expired: number };
    thumbnail: { total: number; active: number; completed: number; expired: number };
    gallery: { poolExists: boolean; activeUsers: number; useCount: number };
    totalSessions: number;
    timestamp: string;
    recommendations: string[];
  };
}

export interface CredentialsCache {
  credentials: S3Credentials;
  expiresAt: Date;
  lastUsed: Date;
}

/**
 * S3 Credentials Manager - Handles all S3 session management with automatic cleanup
 */
export class S3CredentialsManager {
  private static readonly MAX_THUMBNAIL_SESSIONS = 10;
  private static readonly MAX_SESSION_AGE_MS = 30 * 60 * 1000; // 30 minutes
  // Session tracking
  private galleryCredentials: CredentialsCache | null = null;
  private thumbnailSessions = new Map<string, CredentialsCache>();
  private cleanupInterval: NodeJS.Timeout | null = null;
  


  constructor() {
    // Start cleanup interval when first instance is created
    this.startCleanupInterval();
    
    // Register cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.cleanupAllSessions());

      // ADD visibility change handler
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }


  /**
   * Make API request with proper headers
   */
  private async apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
      const response = await fetch(`${UPLOAD_CONFIG.apiBaseUrl}${endpoint}`, {
      ...options,
      credentials: 'include', // send HttpOnly session cookie
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),        // DO NOT set Authorization
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || errorJson.message || `HTTP ${response.status}`);
      } catch (parseError) {
        throw new Error(`API call failed [${response.status}]: ${errorText.substring(0, 200)}`);
      }
    }

    return response.json();
  }

  /**
   * Check if credentials are still valid (with 5 minute buffer)
   */
  private isCredentialsValid(cache: CredentialsCache): boolean {
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    return cache.expiresAt > fiveMinutesFromNow;
  }

  /**
 * Cleanup oldest sessions when limit is reached
 */
  private cleanupOldestSessions(count: number): void {
    const sessions = Array.from(this.thumbnailSessions.entries())
      .sort(([,a], [,b]) => a.lastUsed.getTime() - b.lastUsed.getTime());
    
    for (let i = 0; i < count && i < sessions.length; i++) {
      const [folderPath] = sessions[i];
      console.log(`[S3CredentialsManager] 🧹 Removing oldest session: ${folderPath}`);
      this.thumbnailSessions.delete(folderPath);
      
      // Attempt to complete the session on backend
      this.completeThumbnailSession(folderPath).catch(err => 
        console.warn(`[S3CredentialsManager] Failed to complete removed session:`, err)
      );
    }
  }

  /**
   * Create credentials cache entry
   */
  private createCredentialsCache(credentials: S3Credentials): CredentialsCache {
    return {
      credentials,
      expiresAt: new Date(credentials.Expiration),
      lastUsed: new Date()
    };
  }

  // ===== UPLOAD CREDENTIALS =====

  /**
   * Get upload credentials (no caching - each upload gets fresh credentials)
   */
  async getUploadCredentials(folderPath: string, fileCount: number): Promise<UploadCredentialsResponse> {
    try {
      console.log(`[S3CredentialsManager] 📤 Getting upload credentials for: ${folderPath}`);
      
      const response = await this.apiRequest<UploadCredentialsResponse>('/api/uploads/credentials', {
        method: 'POST',
        body: JSON.stringify({ folderName: folderPath, fileCount })
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to get upload credentials');
      }

      console.log(`[S3CredentialsManager] ✅ Upload credentials obtained for: ${folderPath}`);
      return response;
    } catch (error) {
      console.error(`[S3CredentialsManager] Get upload credentials failed:`, error);
      throw error;
    }
  }

  // ===== GALLERY CREDENTIALS WITH POOLING =====

  /**
   * Get gallery credentials (cached and pooled across users)
   */
  async getGalleryCredentials(): Promise<GalleryCredentialsResponse> {
    try {
      // Check if we have valid cached credentials
      if (this.galleryCredentials && this.isCredentialsValid(this.galleryCredentials)) {
        console.log(`[S3CredentialsManager] 🔄 Reusing cached gallery credentials`);
        this.galleryCredentials.lastUsed = new Date();
        
        return {
          success: true,
          data: {
            credentials: this.galleryCredentials.credentials,
            bucket: 'ar-trial', // You might want to make this configurable
            region: 'us-west-1', // You might want to make this configurable
            sessionType: 'gallery',
            pooled: true
          }
        };
      }

      console.log(`[S3CredentialsManager] 🖼️ Getting fresh gallery credentials...`);
      
      const response = await this.apiRequest<GalleryCredentialsResponse>('/api/uploads/gallery/credentials', {
        method: 'POST'
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to get gallery credentials');
      }

      // Cache the credentials
      this.galleryCredentials = this.createCredentialsCache(response.data.credentials);
      
      console.log(`[S3CredentialsManager] ✅ Gallery credentials obtained and cached`);
      return response;
    } catch (error) {
      console.error(`[S3CredentialsManager] Get gallery credentials failed:`, error);
      return this.handleSessionError('getGalleryCredentials', error);
    }
  }

  /**
   * Release gallery session
   */
  async releaseGallerySession(): Promise<void> {
    try {
      if (!this.galleryCredentials) {
        console.log(`[S3CredentialsManager] No gallery session to release`);
        return;
      }

      console.log(`[S3CredentialsManager] 👋 Releasing gallery session...`);
      
      const response = await this.apiRequest<{ success: boolean; message: string }>('/api/uploads/gallery/release', {
        method: 'POST'
      });

      if (response.success) {
        this.galleryCredentials = null;
        console.log(`[S3CredentialsManager] ✅ Gallery session released`);
      }
    } catch (error) {
      console.error(`[S3CredentialsManager] Release gallery session failed:`, error);
      // Still clear local cache
      this.galleryCredentials = null;
    }
  }

  // ===== THUMBNAIL CREDENTIALS WITH FOLDER-BASED REUSE =====

  /**
   * Get thumbnail credentials (cached per folder)
   */
  async getThumbnailCredentials(folderPath: string): Promise<ThumbnailCredentialsResponse> {
    try {

      if (this.thumbnailSessions.size >= S3CredentialsManager.MAX_THUMBNAIL_SESSIONS) {
      console.warn(`[S3CredentialsManager] ⚠️ Session limit reached (${this.thumbnailSessions.size}), cleaning up oldest`);
      this.cleanupOldestSessions(1);
       }

      // Check if we have valid cached credentials for this folder
      const cached = this.thumbnailSessions.get(folderPath);
    if (cached) {
      if (this.isCredentialsValid(cached)) {
        console.log(`[S3CredentialsManager] 🔄 Reusing cached thumbnail credentials for: ${folderPath}`);
        cached.lastUsed = new Date();
        
        return {
          success: true,
          data: {
            credentials: cached.credentials,
            bucket: 'ar-trial',
            region: 'us-west-1',
            folderPath,
            sessionType: 'thumbnail'
          }
        };
      } else {
        // Credentials expired, refresh them
        console.log(`[S3CredentialsManager] ⏰ Cached credentials expired for: ${folderPath}, refreshing...`);
        return this.refreshExpiredCredentials(folderPath);
      }
    }

      console.log(`[S3CredentialsManager] 🖼️ Getting fresh thumbnail credentials for: ${folderPath}`);
      
      const response = await this.apiRequest<ThumbnailCredentialsResponse>('/api/uploads/thumbnail/credentials', {
        method: 'POST',
        body: JSON.stringify({ folderPath })
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to get thumbnail credentials');
      }

      // Cache the credentials
      this.thumbnailSessions.set(folderPath, this.createCredentialsCache(response.data.credentials));
      
      console.log(`[S3CredentialsManager] ✅ Thumbnail credentials obtained and cached for: ${folderPath}`);
      return response;
    } catch (error) {
      console.error(`[S3CredentialsManager] Get thumbnail credentials failed for ${folderPath}:`, error);
      return this.handleSessionError(`getThumbnailCredentials for ${folderPath}`, error);
    }
  }

  /**
 * Refresh expired credentials for a folder
 */
    private async refreshExpiredCredentials(folderPath: string): Promise<ThumbnailCredentialsResponse> {
      console.log(`[S3CredentialsManager] 🔄 Refreshing expired credentials for: ${folderPath}`);
      
      // Remove expired session
      this.thumbnailSessions.delete(folderPath);
      
      // Get fresh credentials
      return this.getThumbnailCredentials(folderPath);
    }

/**
 * Handle page visibility changes for cleanup
 */
    private handleVisibilityChange = (): void => {
      if (document.hidden) {
        console.log('[S3CredentialsManager] 👁️ Page hidden, scheduling cleanup...');
        
        // Schedule cleanup after page has been hidden for a while
        setTimeout(() => {
          if (document.hidden) {
            console.log('[S3CredentialsManager] 🧹 Page still hidden, cleaning up non-essential sessions...');
            
            // Clean up old sessions but keep recent ones
            const cutoffTime = Date.now() - (5 * 60 * 1000); // 5 minutes ago
            
            for (const [folderPath, cache] of this.thumbnailSessions.entries()) {
              if (cache.lastUsed.getTime() < cutoffTime) {
                this.thumbnailSessions.delete(folderPath);
                this.completeThumbnailSession(folderPath).catch(err => 
                  console.warn(`[S3CredentialsManager] Background cleanup failed for ${folderPath}:`, err)
                );
              }
            }
          }
        }, 30000); // 30 seconds after hiding
      } else {
        console.log('[S3CredentialsManager] 👁️ Page visible again');
      }
    };

  /**
   * Complete thumbnail session for a folder
   */
  async completeThumbnailSession(folderPath: string): Promise<void> {
    try {
      if (!this.thumbnailSessions.has(folderPath)) {
        console.log(`[S3CredentialsManager] No thumbnail session for: ${folderPath}`);
        return;
      }

      console.log(`[S3CredentialsManager] 🏁 Completing thumbnail session for: ${folderPath}`);
      
      const response = await this.apiRequest<{ success: boolean; message: string }>('/api/uploads/thumbnail/complete', {
        method: 'POST',
        body: JSON.stringify({ folderPath })
      });

      if (response.success) {
        this.thumbnailSessions.delete(folderPath);
        console.log(`[S3CredentialsManager] ✅ Thumbnail session completed for: ${folderPath}`);
      }
    } catch (error) {
      console.error(`[S3CredentialsManager] Complete thumbnail session failed for ${folderPath}:`, error);
      // Still remove from local cache
      this.thumbnailSessions.delete(folderPath);
    }
  }

  // ===== SESSION MONITORING =====

  /**
   * Get session statistics from backend
   */
  async getSessionStats(): Promise<SessionStatsResponse> {
    try {
      console.log(`[S3CredentialsManager] 📊 Getting session statistics...`);
      
      const response = await this.apiRequest<SessionStatsResponse>('/api/uploads/sessions/stats');

      if (!response.success || !response.data) {
        throw new Error('Failed to get session statistics');
      }

      console.log(`[S3CredentialsManager] ✅ Session statistics retrieved`);
      return response;
    } catch (error) {
      console.error(`[S3CredentialsManager] Get session statistics failed:`, error);
      throw error;
    }
  }

  /**
   * Force session cleanup on backend
   */
  async forceSessionCleanup(): Promise<{ success: boolean; data: { cleanedSessions: number; timestamp: string } }> {
    try {
      console.log(`[S3CredentialsManager] 🧹 Forcing backend session cleanup...`);
      
      const response = await this.apiRequest<{ success: boolean; data: { cleanedSessions: number; timestamp: string } }>('/api/uploads/sessions/cleanup', {
        method: 'POST'
      });

      if (!response.success) {
        throw new Error('Failed to force session cleanup');
      }

      console.log(`[S3CredentialsManager] ✅ Backend cleanup completed: ${response.data.cleanedSessions} sessions cleaned`);
      return response;
    } catch (error) {
      console.error(`[S3CredentialsManager] Force session cleanup failed:`, error);
      throw error;
    }
  }

  // ===== AUTOMATIC CLEANUP =====

  /**
   * Start automatic cleanup interval
   */
  private startCleanupInterval(): void {
    if (this.cleanupInterval) return;
    
    // Clean up expired local cache every 2 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredCache();
    }, 2 * 60 * 1000);
    
    console.log(`[S3CredentialsManager] 🔄 Automatic cleanup interval started`);
  }

  /**
   * Clean up expired cached credentials
   */
  private cleanupExpiredCache(): void {
    const now = new Date();
    let cleaned = 0;

    // Clean up expired gallery credentials
    if (this.galleryCredentials && this.galleryCredentials.expiresAt <= now) {
      this.galleryCredentials = null;
      cleaned++;
      console.log(`[S3CredentialsManager] 🧹 Cleaned expired gallery credentials`);
    }

    // Clean up expired thumbnail credentials
    for (const [folderPath, cache] of this.thumbnailSessions.entries()) {
      if (cache.expiresAt <= now) {
        this.thumbnailSessions.delete(folderPath);
        cleaned++;
        console.log(`[S3CredentialsManager] 🧹 Cleaned expired thumbnail credentials for: ${folderPath}`);
      }
    }

    if (cleaned > 0) {
      console.log(`[S3CredentialsManager] 🧹 Automatic cleanup: ${cleaned} cached credentials removed`);
    }
  }

  /**
   * Cleanup all sessions (both local cache and backend)
   */
  async cleanupAllSessions(): Promise<void> {
    console.log(`[S3CredentialsManager] 🧹 Cleaning up all sessions...`);
    
    try {
      // Release gallery session
      if (this.galleryCredentials) {
        await this.releaseGallerySession();
      }

      // Complete all thumbnail sessions
      const thumbnailPromises = Array.from(this.thumbnailSessions.keys()).map(folderPath => 
        this.completeThumbnailSession(folderPath).catch(err => 
          console.warn(`Failed to complete thumbnail session for ${folderPath}:`, err)
        )
      );

      await Promise.all(thumbnailPromises);

      console.log(`[S3CredentialsManager] ✅ All sessions cleaned up`);
    } catch (error) {
      console.error(`[S3CredentialsManager] Error during session cleanup:`, error);
    }
  }

  /**
   * Get local cache status (for debugging)
   */
  getCacheStatus(): {
    gallery: { active: boolean; expiresAt?: string; lastUsed?: string };
    thumbnails: Array<{ folderPath: string; expiresAt: string; lastUsed: string }>;
  } {
    return {
      gallery: this.galleryCredentials ? {
        active: true,
        expiresAt: this.galleryCredentials.expiresAt.toISOString(),
        lastUsed: this.galleryCredentials.lastUsed.toISOString()
      } : { active: false },
      thumbnails: Array.from(this.thumbnailSessions.entries()).map(([folderPath, cache]) => ({
        folderPath,
        expiresAt: cache.expiresAt.toISOString(),
        lastUsed: cache.lastUsed.toISOString()
      }))
    };
  }

  /**
 * Handle session errors consistently
 */
private async handleSessionError(operation: string, error: unknown): Promise<never> {
  console.error(`[S3CredentialsManager] ${operation} failed:`, error);
  
  // Clear potentially corrupted local cache
  if (operation.includes('gallery')) {
    this.galleryCredentials = null;
  }
  
  if (operation.includes('thumbnail')) {
    // Clear all thumbnail sessions if there's a systemic issue
    this.thumbnailSessions.clear();
  }
  
  throw error;
}

  /**
   * Destroy the credentials manager (cleanup all resources)
   */
  destroy(): void {
    console.log(`[S3CredentialsManager] 💀 Destroying credentials manager...`);
    
    // Stop cleanup interval
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // ADD: Remove visibility change listener
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    // Clear all cached credentials
    this.galleryCredentials = null;
    this.thumbnailSessions.clear();

    console.log(`[S3CredentialsManager] 💀 Credentials manager destroyed`);
  }

    /**
   * Get session metrics for monitoring
   */
  getSessionMetrics(): {
    activeThumbnailSessions: number;
    hasActiveGallerySession: boolean;
    oldestSessionAge: number | null;
    totalMemoryUsage: number;
  } {
    let oldestAge: number | null = null;
    let memoryUsage = 0;

    // Calculate oldest session age
    for (const cache of this.thumbnailSessions.values()) {
      const age = Date.now() - cache.lastUsed.getTime();
      if (oldestAge === null || age > oldestAge) {
        oldestAge = age;
      }
      memoryUsage += JSON.stringify(cache).length; // Rough memory estimate
    }

    if (this.galleryCredentials) {
      const galleryAge = Date.now() - this.galleryCredentials.lastUsed.getTime();
      if (oldestAge === null || galleryAge > oldestAge) {
        oldestAge = galleryAge;
      }
      memoryUsage += JSON.stringify(this.galleryCredentials).length;
    }

    return {
      activeThumbnailSessions: this.thumbnailSessions.size,
      hasActiveGallerySession: !!this.galleryCredentials,
      oldestSessionAge: oldestAge,
      totalMemoryUsage: memoryUsage
    };
  }

}


// Export singleton instance
export const s3CredentialsManager = new S3CredentialsManager();
export default s3CredentialsManager;